// The address and the board, one rule (2026-10-19). The review address names
// a game ply in its fragment, `#ply=N`, so a reload or a shared link opens
// the same position. Two things happen, each a pure function here:
//
// - The board moved (`onState`): the review writes the fragment, replacing
//   the history entry, so the back button leaves the game rather than
//   undoing moves. A line of the reader's own, or a puzzle, writes the game
//   ply it branches from. The fragment written is remembered as pending.
// - A location was reached (`onLocation`): if its hash is one the review
//   wrote, it is that write's echo from the router, nothing to do; any other
//   location is a request from the reader, a link followed or the browser's
//   back and forward, and the board goes to the ply it names, from a line
//   or a look back as well (`go` drops them). The first render asks for
//   nothing: opening the game places the board at the address's ply.
//
// Before, three rules shared this (`shouldGo`, `isDeliberate`, `addressUpdate`):
// the board ignored every address change while on a line, so that the
// review's own writes would not pull it off the line, and a link followed
// from a line changed the address and left the board at the line's end; a
// first fix told the reader's changes apart by the router's navigation type.
// Telling the review's own writes apart by their hash needs no router.
import { addressPly, fragmentFor, normalHash, plyOf } from './ply-fragment.ts';

export interface AddressState {
  game?: { id: string };
  ply: number;
  exploring?: { fromPly: number };
}

/** The fragments the review wrote whose echo from the router has not come yet, oldest first. */
export interface AddressSync {
  readonly pending: readonly string[];
}

export const NO_WRITES: AddressSync = { pending: [] };
/** How many writes may wait for their echo; older ones are forgotten. */
const PENDING_MAX = 8;

export type LocationAction = { kind: 'go'; ply: number } | { kind: 'echo' } | { kind: 'none' };

/** A location was reached: whether to move the board, and the pending writes after it. */
export function onLocation(
  sync: AddressSync,
  input: { hash: string; first: boolean; state: AddressState; gameId: string | undefined },
): { action: LocationAction; sync: AddressSync } {
  const hash = normalHash(input.hash);
  const i = sync.pending.lastIndexOf(hash);
  if (i >= 0) return { action: { kind: 'echo' }, sync: { pending: sync.pending.slice(i + 1) } };
  if (input.first || !input.gameId || input.state.game?.id !== input.gameId) return { action: { kind: 'none' }, sync };
  return { action: { kind: 'go', ply: plyOf(hash) ?? 0 }, sync };
}

/**
 * The review's state changed: the fragment to write, or none. None before
 * the address's own ply has been applied (`settled`, or the opening position
 * would overwrite it), for another game than the screen's, with no game
 * open, or when the address already says it. An empty string removes the
 * fragment (the starting position).
 */
export function onState(
  sync: AddressSync,
  input: { state: AddressState; gameId: string | undefined; settled: boolean; hash: string },
): { write?: string; sync: AddressSync } {
  if (!input.settled || !input.gameId || input.state.game?.id !== input.gameId) return { sync };
  const at = addressPly(input.state);
  if (at === undefined) return { sync };
  const want = fragmentFor(at);
  if (want === normalHash(input.hash)) return { sync };
  return { write: want, sync: { pending: [...sync.pending, want].slice(-PENDING_MAX) } };
}
